/**
 *  Use this type for files that are not yet stored on the backend, and thus have no id.
 *  E.g. files that will be uploaded.
 */
export type PendingFile = File & {
  toBeDeleted?: boolean; // used only on FE to mark files for deletion
};

/**
 * Use this type for files that are already stored on the backend, and thus have an id.
 * E.g. files that have been uploaded and saved.
 */
export type PersistedFile = File & {
  id: number;
  toBeDeleted?: boolean; // used only on FE to mark files for deletion
};

/**
 * Use this union type when a file can be either pending or persisted.
 * This is useful when handling mixed lists of files, e.g. in upload components.
 */
export type ManagedFile = PendingFile | PersistedFile;

/** Type guard to check if a ManagedFile is a PendingFile (i.e., does not have an id). */
export const isPendingFile = (file: ManagedFile): file is PendingFile => file && !('id' in file);

/** Type guard to check if a ManagedFile is a PersistedFile (i.e., has an id). */
export const isPersistedFile = (file: ManagedFile): file is PersistedFile => file && 'id' in file;

/** Type guard to check if a ManagedFile is marked for deletion and is a PersistedFile => needs a DELETE request. */
export const isDeletedFile = (file: ManagedFile): file is PersistedFile =>
  isPersistedFile(file) && file.toBeDeleted === true;

// extend the built-in FileSystemEntry type with the childrenEntries property
export type EntryExtension = { entry: FileSystemEntry; childrenEntries: EntryExtension[] };

// every FileSystemEntry is mapped to a File or a Directory
export type UploadableContent = File | Directory;

export type Directory = {
  size: number; // size of all content inside folder
  totalFileCount: number; // recursive count of all files in the directory
  isDirectory: true;
  name: string;
  children: UploadableContent[];
};

export const uploadableContentIsFile = (content: UploadableContent): content is File =>
  content && !(content as Directory)?.isDirectory;
export const uploadableContentIsDirectory = (content: UploadableContent): content is Directory =>
  content && (content as Directory)?.isDirectory;

export type UploadMetaData = {
  company_picture?: string | number;
  profile_picture?: string | number;
  user_drive_folder_id?: number;
  user_drive_file_id?: number;
  progress_item_checklist_item_id?: number;
  message_id?: number;
  commitment_id?: number;
  bid_id?: number;
  pc_id?: number;
  pco_id?: number;
  cor_id?: number;
  pcco_id?: number;
  pi_id?: number;
  rfi?: {
    id: number;
    belongs_to: 'question' | 'answer';
  };
  submittal_package?: {
    id: number;
    doc_type: 'reviewed' | 'source' | 'submitted';
  };
  name: string;
  size: number;
  type: string; // file type
};

export type FileWithMetadata = {
  file: File;
  metadata: Partial<UploadMetaData>;
};

export type PresignedUrlResp = {
  file_id: number;
  presigned_url: string;
};

export type FileUploadResp = {
  file_id: number;
  presigned_url: string;
  message: string;
  error?: any;
};

export type FileUploadStatus = {
  totalBytes: number;
  loadedBytes: number;
  completedWithSuccess: boolean; // completed means all bytes are uploaded
  completedWithError: boolean; // an error occurred during upload
  validated?: boolean; // if the file was validated after upload
};

export type FileUploadTotals = {
  loadedBytes: number;
  percent: number;
  uploadedCount: number;
  fileCount: number;
  errorCount: number;
  completed: boolean; // completed with success OR error
  completedWithError: boolean;
};

export type FileConstantTotals = {
  totalBytes: number;
  fileCount: number;
};

export type UploadStatusMap = Record<string, FileUploadStatus>;

export enum FILE_VALIDATION_STATUS {
  PENDING,
  SUCCESS,
  ERROR,
}
export type FileEvent = {
  file: ManagedFile;
  index: number;
};

// Recursive structure to represent folders and files inside a zip
export type ZipStructure = {
  files: { id: string | number; name: string }[]; // represents the files in the current folder
  children: ZipStructure[]; // in case of folders represents the folder's children
  id?: string | number;
  name?: string;
  size?: number; // only on the root level
  parentFolderId?: number | string;
};

export type MakeCopyStructure = {
  parent_id: number | string;
  children: ZipStructure[];
  files: { id: string | number }[];
};
